import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import { capitalizeWords } from '../../utils/stringUtils';
import { LEAD_SOURCE_FILTER_OPTIONS } from '../../constants/leadSourceOptions';

const occupationOptions = [
  { value: 'Salaried', label: 'Salaried' },
  { value: 'Self employed', label: 'Self Employed' },
  { value: 'Business', label: 'Business' },
  { value: 'Govt employee', label: 'Government Employee' },
  { value: 'Others', label: 'Others' }
];

const sourceOptions = [
  { value: '', label: 'Select Source', disabled: true, selected: true },
  ...LEAD_SOURCE_FILTER_OPTIONS.filter(
    (opt) => !['facebook', 'website', 'whatsapp_outbound', 'self'].includes(opt.value)
  ).map((opt) => ({ value: opt.value, label: opt.label }))
];

const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  phone_number: yup.string().required('Phone number is required'),
  alternate_phone_number: yup.string(),
  email: yup.string().email('Enter a valid email').nullable(),
  city: yup.string(),
  state: yup.string(),
  pincode: yup.string(),
  occupation: yup.string(),
  designation: yup.string(),
  company_name: yup.string(),
  reference_name: yup.string(),
  source: yup.string()
});

function emptyToBlank(value) {
  if (value == null || value === '---') return '';
  return String(value);
}

const EditContactProfileForm = ({ lead, onSubmit, onCancel, isSubmitting = false }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      phone_number: '',
      alternate_phone_number: '',
      email: '',
      city: '',
      state: '',
      pincode: '',
      occupation: '',
      designation: '',
      company_name: '',
      reference_name: '',
      source: ''
    }
  });

  useEffect(() => {
    if (!lead) return;
    const email =
      lead?.user?.email || lead?.official_email || lead?.personal_email || '';
    reset({
      name: emptyToBlank(lead?.user?.name),
      phone_number: emptyToBlank(lead?.user?.phone_number),
      alternate_phone_number: emptyToBlank(lead?.alternate_phone_number),
      email: emptyToBlank(email),
      city: emptyToBlank(lead?.address?.city),
      state: emptyToBlank(lead?.address?.state),
      pincode: emptyToBlank(lead?.address?.zipCode),
      occupation: emptyToBlank(lead?.occupation),
      designation: emptyToBlank(lead?.designation),
      company_name: emptyToBlank(lead?.company_name),
      reference_name: emptyToBlank(lead?.reference_name || lead?.reference),
      source: emptyToBlank(lead?.source)
    });
  }, [lead, reset]);

  const handlePincodeChange = useCallback(
    async (value) => {
      const cleanedValue = value?.replace(/[^0-9]/g, '')?.slice(0, 6) || '';
      setValue('pincode', cleanedValue);

      if (cleanedValue.length === 6) {
        try {
          const response = await fetch(`https://api.postalpincode.in/pincode/${cleanedValue}`);
          const data = await response.json();
          if (data[0]?.Status === 'Success' && data[0]?.PostOffice?.[0]) {
            const postOffice = data[0].PostOffice[0];
            setValue('city', postOffice.District || '');
            setValue('state', postOffice.State || '');
          }
        } catch (error) {
          console.error('Error fetching pincode data:', error);
        }
      }
    },
    [setValue]
  );

  const handleFormSubmit = (data) => {
    const email = data.email?.trim() || null;
    const payload = {
      name: capitalizeWords(data.name?.trim() || ''),
      phone_number: data.phone_number?.trim() || '',
      alternate_phone_number: data.alternate_phone_number?.trim() || null,
      email,
      official_email: email,
      city: data.city?.trim() || '',
      state: data.state?.trim() || '',
      pincode: data.pincode?.trim() || '',
      occupation: data.occupation?.trim() || null,
      designation: data.designation?.trim() || null,
      company_name: data.company_name?.trim() || null,
      reference_name: data.reference_name?.trim() || null,
      source: data.source?.trim() || null
    };
    onSubmit?.(payload);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-4">
      <p className="text-sm text-gray-600">
        Fill in missing contact details for this lead. Leave a field blank to clear it.
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormInput
          width={100}
          label="Name"
          register={register}
          register_key="name"
          type="text"
          isRequired
          errors={errors?.name}
          onType={(text) => setValue('name', capitalizeWords(text))}
        />
        <FormInput
          width={100}
          label="Phone"
          register={register}
          register_key="phone_number"
          type="text"
          isRequired
          errors={errors?.phone_number}
        />
        <FormInput
          width={100}
          label="Alternate phone"
          register={register}
          register_key="alternate_phone_number"
          type="text"
          errors={errors?.alternate_phone_number}
        />
        <FormInput
          width={100}
          label="Email"
          register={register}
          register_key="email"
          type="text"
          errors={errors?.email}
        />
        <FormInput
          width={100}
          label="Pincode"
          register={register}
          register_key="pincode"
          type="text"
          errors={errors?.pincode}
          onType={handlePincodeChange}
        />
        <FormInput
          width={100}
          label="City"
          register={register}
          register_key="city"
          type="text"
          errors={errors?.city}
        />
        <FormInput
          width={100}
          label="State"
          register={register}
          register_key="state"
          type="text"
          errors={errors?.state}
        />
        <FormInput
          width={100}
          label="Occupation"
          register={register}
          register_key="occupation"
          type="dropdown"
          defaultValuePre=""
          options={[
            { value: '', label: 'Select Occupation', disabled: true, selected: true },
            ...occupationOptions
          ]}
          errors={errors?.occupation}
        />
        <FormInput
          width={100}
          label="Designation"
          register={register}
          register_key="designation"
          type="text"
          errors={errors?.designation}
          onType={(text) => setValue('designation', capitalizeWords(text))}
        />
        <FormInput
          width={100}
          label="Company"
          register={register}
          register_key="company_name"
          type="text"
          errors={errors?.company_name}
          onType={(text) => setValue('company_name', capitalizeWords(text))}
        />
        <FormInput
          width={100}
          label="Referred by"
          register={register}
          register_key="reference_name"
          type="text"
          errors={errors?.reference_name}
          onType={(text) => setValue('reference_name', capitalizeWords(text))}
        />
        <FormInput
          width={100}
          label="Source"
          register={register}
          register_key="source"
          type="dropdown"
          defaultValuePre=""
          options={sourceOptions}
          errors={errors?.source}
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" displayType="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" displayType="primary" disabled={isSubmitting} isLoading={isSubmitting}>
          Save contact profile
        </Button>
      </div>
    </form>
  );
};

export default EditContactProfileForm;
